import { DEFAULT_NEXUS_DATA } from "src/consts/nexus";
import { NexusData } from "src/types/nexus";
import { create } from "zustand";

type State = {
  nexus: NexusData;
  ready: boolean;
  /**
   * The Nexus `device_id` whose response is currently in `nexus`, or null before the first load.
   *
   * `ready` alone is not enough to act on a flag irreversibly. Bootstrap loads Nexus TWICE — once
   * anonymously as "ffb", then again with the company id once user data arrives — and `ready` flips
   * on the first of those. A consumer that redirects or denies access on a false flag must wait for
   * the company-scoped response, or it acts on the anon default. Compare this against the id the
   * app intends to load (`user.company.company_id || "ffb"`) to know the answer is final.
   */
  loadedCompanyId: string | null;
  setNexus: (nexus: NexusData, companyId?: string) => void;
  getNexusPayload: <K extends keyof NexusData>(groupKey: K) => NexusData[K]["payload"];
};

export const nexusStore = create<State>((set, get) => ({
  nexus: DEFAULT_NEXUS_DATA,
  ready: false,
  loadedCompanyId: null,
  setNexus: (nexus: NexusData, companyId?: string) =>
    set({ nexus, ready: true, ...(companyId === undefined ? {} : { loadedCompanyId: companyId }) }),
  getNexusPayload: <K extends keyof NexusData>(groupKey: K): NexusData[K]["payload"] => {
    const nexus = get().nexus ?? DEFAULT_NEXUS_DATA;
    const group = nexus[groupKey];

    if (!group || !group.payload) {
      return DEFAULT_NEXUS_DATA[groupKey].payload as NexusData[K]["payload"];
    }

    return group.payload as NexusData[K]["payload"];
  }
}));
